import React, { useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { motion } from "framer-motion/dist/framer-motion";
import OutsideClickHandler from "react-outside-click-handler";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";

import {
  canExportSampleSubmissionData,
  hasPermission,
  permissions,
} from "../../../../utils/Permissions";
import { buildCreateSampleSubmissionPath } from "../../utils";

import { getEnvironmentFields } from "../../../../actions/envAnalytics";
import { getProductFields } from "../../../../actions/reports";

import { ReactComponent as EnvironmentIcon } from "../../../../assets/images/feature-icons/EnvironmentIconFilled.svg";
import { ReactComponent as ProductIcon } from "../../../../assets/images/feature-icons/ProductIconFilled.svg";
import { ReactComponent as EventExportIcon } from "../../../../assets/images/sample-submission/EventExportIcon.svg";
import { ReactComponent as ShareIcon } from "../../../../assets/images/sample-submission/Share.svg";

import "./FloatingButton.css";

const FloatingButton = ({
  setShareSubmission,
  setExportData,
  setShowSubmissionForm,
  setSampleCategory,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const history = useHistory();
  const { areResultsLocked } = useAccessStatus();

  const canShare = hasPermission(permissions.sampleSubmission.share);
  const showShare = hasPermission(permissions.sampleSubmission.createProductSubmission);
  const showExportData = canExportSampleSubmissionData() && !areResultsLocked;

  const canCreateProductMenu = hasPermission(permissions.sampleSubmission.createProductSubmission)
    || hasPermission(permissions.onboarding.onboardProduct);
  const canCreateEnvironmentMenu = hasPermission(permissions.sampleSubmission.createEnvironmentSubmission)
    || hasPermission(permissions.onboarding.onboardEnvironment);

  /*
   * Earlier logic : useEffect on mount called get*Fields to show/hide create menu items
   * from onboarding status (hide when onboarded without create permission).
   * Commented out: avoids API calls on every render mount; uses sync create/onboard checks and
   * handleOnboardingRedirect on click instead (aligned with CreateSampleModal).
   */
  // useEffect(() => {
  //   let cancelled = false;
  //   const checkCreateAvailability = async () => {
  //     let productMenu = false;
  //     if (hasPermission(permissions.sampleSubmission.createProductSubmission)) {
  //       productMenu = true;
  //     } else {
  //       const productFieldsResult = await getProductFields(true);
  //       const productOnboardingDone = productFieldsResult.success && productFieldsResult.fields && productFieldsResult.fields.length > 0;
  //       if (productOnboardingDone) {
  //         productMenu = false;
  //       } else if (hasRolePermission("onboard_product")) {
  //         productMenu = true;
  //       } else {
  //         productMenu = false;
  //       }
  //     }
  //
  //     let environmentMenu = false;
  //     if (hasPermission(permissions.sampleSubmission.createEnvironmentSubmission)) {
  //       environmentMenu = true;
  //     } else {
  //       const envFieldsResult = await getEnvironmentFields(true);
  //       const envOnboardingDone = envFieldsResult.success && envFieldsResult.fields && envFieldsResult.fields.length > 0;
  //       if (envOnboardingDone) {
  //         environmentMenu = false;
  //       } else if (hasRolePermission("onboard_environment")) {
  //         environmentMenu = true;
  //       } else {
  //         environmentMenu = false;
  //       }
  //     }
  //
  //     if (!cancelled) {
  //       setCanCreateProductMenu(productMenu);
  //       setCanCreateEnvironmentMenu(environmentMenu);
  //     }
  //   };
  //
  //   checkCreateAvailability();
  //   return () => { cancelled = true; };
  // }, []);

  const openMenu = () => setIsOpen(true);

  const closeMenu = () => {
    setIsOpen(false);
    setHoveredOption(null);
  };

  const handleMouseLeave = () => setHoveredOption(null);
  const handleOutsideClick = () => {
    closeMenu();
  };

  const handleOnboardingRedirect = async (category) => {
    const result = category === "environment"
      ? await getEnvironmentFields(true)
      : await getProductFields(true);
    const onboardingDone = result.success && result.fields && result.fields.length > 0;
    if (onboardingDone) {
      toast.error(
        category === "environment"
          ? "You do not have access to create an Environment Sample Submission."
          : "You do not have access to create a Product Sample Submission.",
      );
    } else {
      history.push(`/onboarding/${category}`);
    }
  };

  const handleOptionClick = (option) => {
    if (option === "share") setShareSubmission(true);
    if (option === "export" && !areResultsLocked) setExportData(true);
    if (option === "create_product") {
      if (hasPermission(permissions.sampleSubmission.createProductSubmission)) {
        setSampleCategory("product");
        setShowSubmissionForm(true);
        history.push(buildCreateSampleSubmissionPath("product"));
      } else {
        handleOnboardingRedirect("product");
      }
    }
    if (option === "create_environment") {
      if (hasPermission(permissions.sampleSubmission.createEnvironmentSubmission)) {
        setSampleCategory("environment");
        setShowSubmissionForm(true);
        history.push(buildCreateSampleSubmissionPath("environment"));
      } else {
        handleOnboardingRedirect("environment");
      }
    }
    closeMenu();
  };

  const handleOptionHover = (option) => setHoveredOption(option);

  const createEntries = [];
  if (canCreateEnvironmentMenu) {
    createEntries.push({
      icon: <EnvironmentIcon width={18} height={18} title="environment sample submission" />,
      text: "Environment Sample Submission",
      option: "create_environment",
    });
  }
  if (canCreateProductMenu) {
    createEntries.push({
      icon: <ProductIcon width={18} height={18} title="product sample submission" />,
      text: "Product Sample Submission",
      option: "create_product",
    });
  }

  const options = [
    ...(showExportData ? [{ icon: <EventExportIcon width={18} height={18} title="export icon" />, text: "Export Data", option: "export" }] : []),
    ...(canShare && showShare ? [{ icon: <ShareIcon width={18} height={18} title="share icon" />, text: "Share", option: "share" }] : []),
    ...createEntries,
  ];

  const renderOption = ({ text, option, icon }) => {
    const isHovered = hoveredOption === option;
    const baseClassName = icon ? "FloatingButton__Option" : "FloatingButton";
    const hoverClassName = icon ? "FloatingButton__Option--hover" : "FloatingButton__Option_Create--hover";
    const className = `${baseClassName} ${isHovered ? hoverClassName : ""}`;

    return (
      <motion.div
        key={option}
        className={className}
        onMouseOver={() => handleOptionHover(option)}
        onFocus={() => handleOptionHover(option)}
        onMouseLeave={handleMouseLeave}
        onBlur={handleMouseLeave}
        onClick={() => handleOptionClick(option)}
        layout
        transition={{ duration: icon ? 0.14 : 0.08, ease: "easeOut" }}
      >
        {isHovered && <span>{text}</span>}
        {icon || <PlusOutlined className="FloatingButton__PlusIcon" />}
      </motion.div>
    );
  };

  const hasExpandableMenu = options.length > 1;
  const singleOption = options.length === 1 ? options[0] : null;

  if (options.length === 0) {
    return null;
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      {singleOption && !hasExpandableMenu && renderOption(singleOption)}
      {hasExpandableMenu && (
        <div
          className="FloatingButton__HoverAnchor"
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
          onFocus={openMenu}
        >
          <motion.div
            className={`FloatingButton ${isOpen ? "FloatingButton--hover" : ""}`}
            initial={{ rotate: 0 }}
            animate={{ rotate: isOpen ? -45 : 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <PlusOutlined className="FloatingButton__PlusIcon" aria-hidden />
          </motion.div>
          {isOpen && (
            <motion.div
              className="FloatingButton__Options"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onMouseEnter={openMenu}
            >
              {options.map((option) => renderOption(option))}
            </motion.div>
          )}
        </div>
      )}
    </OutsideClickHandler>
  );
};

export default FloatingButton;
